import React from 'react';
import classNames from 'classnames';
import { IconsAccesory } from '@components/ContactCard';
import ContactAvatar from '@components/ContactAvatar';
import ListItem from '@components/ListItem';
import styles from './SearchResult.module.scss';
import { useSelector } from 'react-redux';
import { selectContact } from '@actions/contacts';
import { useWorkspaceContext } from '@support/hooks/use-workspace-context';
import {
  NetworkStrengthPill,
  contactNetworkStrengthsForWorkspace,
} from '@components/Contact/NetworkStrength';
import { networkStrengthIconForBucket } from '@models/Contact';
import { selectIsUpcomingBirthdaysSearch } from '@actions/search';
import { BirthdayBadge, BirthdayDate } from '@components/Contact/Badges/ContactBirthday';

export type SearchHighlightType =
  | 'bio'
  | 'gender'
  | 'fullName'
  | 'displayName'
  | 'searchNotes'
  | 'title'
  | 'organizations.title'
  | 'byline'
  | 'information.value'
  | 'location'
  | 'locations.search'
  | 'educations.name'
  | 'organization'
  | 'organizations.name'
  | 'educations.degree'
  | 'interests.n'
  | 'websites.url'
  | 'twitterHandle'
  | 'organizations.description'
  | 'searchHashtags';

export type SearchHighlightTitle =
  | 'bio'
  | 'byline'
  | 'gender'
  | 'title'
  | 'name'
  | 'notes'
  | 'source'
  | 'location'
  | 'education'
  | 'organization'
  | 'interest'
  | 'website'
  | 'twitter'
  | '#';

export interface Snippet {
  title: SearchHighlightTitle;
  rank: number;
  highlight: string;
}

const rankForType: Record<SearchHighlightType, number> = {
  'byline': 1,

  'information.value': 2,
  'gender': 2,

  'educations.name': 3,
  'educations.degree': 3,

  'location': 4,
  'locations.search': 4,
  'interests.n': 4,

  'organization': 5,
  'organizations.name': 5,
  'organizations.description': 5,

  'title': 6,
  'organizations.title': 6,
  'twitterHandle': 6,
  'websites.url': 6,

  'searchNotes': 7,
  'bio': 8,
  'displayName': 9,
  'fullName': 10,

  'searchHashtags': 11,
};

const titleForType: Record<SearchHighlightType, SearchHighlightTitle> = {
  'bio': 'bio',
  'byline': 'byline',
  'gender': 'gender',
  'title': 'title',
  'organizations.title': 'title',
  'displayName': 'name',
  'fullName': 'name',
  'searchNotes': 'notes',
  'information.value': 'source',
  'location': 'location',
  'locations.search': 'location',
  'educations.name': 'education',
  'educations.degree': 'education',
  'organization': 'organization',
  'organizations.description': 'organization',
  'organizations.name': 'organization',
  'interests.n': 'interest',
  'websites.url': 'website',
  'twitterHandle': 'twitter',
  'searchHashtags': '#',
};

export const MatchDisplay = ({
  title,
  html,
  value,
}: {
  title: string;
  html?: string;
  value?: string;
}) => (
  <span className={styles.match}>
    <span className={styles.matchTitle}>{title}</span>
    {html ? (
      <span className={styles.matchValue} dangerouslySetInnerHTML={{ __html: html }} />
    ) : (
      <span className={styles.matchValue}>{value}</span>
    )}
  </span>
);

function calculateSnippet(snippet: any): Snippet | null {
  if (!snippet) {
    return null;
  }

  const snippets: Snippet[] = Object.entries(snippet).map(([type, matches]) => {
    let highlight = Array.isArray(matches) ? matches[0] : matches;

    if (type === 'searchHashtags' && Array.isArray(matches)) {
      highlight = matches.join('&nbsp;');
    }

    return {
      title: titleForType[type] || type,
      rank: rankForType[type],
      highlight,
    };
  });

  snippets.sort((a, b) => b.rank - a.rank);
  return snippets?.[0] || null;
}

function Match({ snippet }: { snippet: Snippet | null }) {
  if (!snippet) {
    return null;
  }

  snippet.highlight = snippet.highlight.replaceAll('<br>', ' ');
  snippet.highlight = snippet.highlight.replaceAll('\n', ' ');

  return <MatchDisplay title={snippet.title} html={snippet.highlight} />;
}

export default function SearchResult({
  result,
  lookup = false,
  before = null,
  largeContactName = false,
  showSearchMatch = true,
  isMainSearchView = false,
}) {
  const { isMultiPlayer, activeWorkspace } = useWorkspaceContext();
  const contact = useSelector(selectContact(result.id));
  const isUpcomingBirthdaySearch = useSelector(selectIsUpcomingBirthdaysSearch);

  const searchMatch = lookup ? (
    contact.lookup
  ) : (
    <Match snippet={calculateSnippet(contact._snippetResult)} />
  );

  let teamMemberWithHighestNetworkStrength = undefined;
  if (isMultiPlayer) {
    const teamMemberNetworkStrengths = contactNetworkStrengthsForWorkspace(
      contact,
      activeWorkspace,
    );
    teamMemberWithHighestNetworkStrength = teamMemberNetworkStrengths?.[0];
  }

  const NetworkStrengthIcon = networkStrengthIconForBucket(
    contact?.networkStrengthWithUserTeamMembers?.[0]?.bucket,
  );

  let searchMatchDisplay = showSearchMatch && (
    <ListItem.Content fill className={styles.overflowFade}>
      {searchMatch}
    </ListItem.Content>
  );
  if (showSearchMatch && isMainSearchView && isUpcomingBirthdaySearch) {
    searchMatchDisplay = (
      <ListItem.Content fill className={styles.birthday}>
        <BirthdayBadge />
        <BirthdayDate contact={contact} />
      </ListItem.Content>
    );
  }

  return (
    <>
      {before}
      <ListItem.Content
        className={classNames(
          styles.contact,
          styles.overflowFade,
          largeContactName && styles.largeContactName,
        )}
      >
        {contact.displayName}
        <IconsAccesory className="list-item-inline-footer" contact={contact}>
          {!isMultiPlayer && contact.networkStrengthWithUserTeamMembers?.length > 0 && (
            <NetworkStrengthIcon />
          )}
          {isMultiPlayer && showSearchMatch && teamMemberWithHighestNetworkStrength && (
            <NetworkStrengthPill
              bucket={teamMemberWithHighestNetworkStrength!.bucket}
              teamMember={teamMemberWithHighestNetworkStrength!.teamMember}
            />
          )}
        </IconsAccesory>
      </ListItem.Content>
      {searchMatchDisplay}
      {isMultiPlayer && !showSearchMatch && (
        <ListItem.Content
          fill
          className={classNames(styles.overflowFade, styles.networkStrengthDisplay)}
        >
          {teamMemberWithHighestNetworkStrength ? (
            <>
              <NetworkStrengthPill
                bucket={teamMemberWithHighestNetworkStrength!.bucket}
                teamMember={teamMemberWithHighestNetworkStrength!.teamMember}
              />
              {teamMemberWithHighestNetworkStrength!.teamMember!.displayName}
            </>
          ) : null}
        </ListItem.Content>
      )}
      <ListItem.Content>
        <ContactAvatar size="sm" contact={contact} />
      </ListItem.Content>
    </>
  );
}
